/**
 * ============================================================================
 * SITE NAVIGATION — the one place the destination set and its grouping live.
 * ============================================================================
 *
 * THREE COMPONENTS RENDER THIS SITE'S NAVIGATION and until now each held its
 * own copy of the list: `Navbar` desktop, `Navbar` mobile, and `Footer`. Ten
 * destinations x three literals is thirty strings that have to be edited
 * together, and D-067 is the proof that they are not: it added `/products` and
 * `/projects` to all three by hand, which worked, and would have failed
 * silently in exactly one of them.
 *
 * So the set is declared once here and the three call sites consume it. Adding
 * a route is now one edit in this file plus the `ROUTES` entry in
 * `scripts/prerender.mjs` that CLAUDE.md already requires in the same commit.
 *
 * THIS IS NOT CONTENT (Article II.2). The grouping below is an
 * information-architecture judgement about the UI — which labels sit under
 * "Work" is a design decision, not a fact about Sourish's career — so it lives
 * in `constants/`, not in `src/content/data/`. The professional facts these
 * destinations lead TO all come from the content layer, as before.
 *
 * ONLY MOUNTED DESTINATIONS APPEAR HERE (Article II.4). Every `href` below is
 * a section that `Home.tsx` mounts and every `to` is a route that `App.tsx`
 * declares AND `prerender.mjs` writes. Add a link in the same commit that
 * mounts its target, never before — the aspirational set this navigation once
 * carried (Architecture, Platforms, Governance, Leadership, Insights, Contact)
 * was six labels sharing four anchors, none of which described its target.
 *
 * TWO KINDS OF DESTINATION, and they are not interchangeable:
 *   · `href` — a section anchor, ROOT-RELATIVE (`/#about`, never `#about`).
 *     A bare hash resolves against the current path and matches nothing on
 *     `/stories`. Rendered as an `<a>`.
 *   · `to`   — another route. Rendered as a react-router `<Link>`, and it MUST
 *     be, because react-router does not scroll to a hash on client-side
 *     navigation: a `<Link to="/#about">` would silently stop scrolling.
 */

/** Exactly one of `href` (a section anchor) or `to` (a route) is set. */
export interface NavDestination {
	label: string;
	href?: string;
	to?: string;
}

export interface NavGroup {
	/** Stable, used to build the trigger/menu id pair — never randomised. */
	id: string;
	label: string;
	items: ReadonlyArray<NavDestination>;
}

/**
 * Top-level, ungrouped. "About" is the page's own opening and has no siblings
 * that belong beside it, so a one-item dropdown would be a control that can
 * only ever say one thing — the same reasoning that left `/products` without a
 * category filter in D-067.
 */
export const NAV_ABOUT: NavDestination = { label: "About", href: "/#about" };

/**
 * The three groups, in the owner-approved order and membership (D-068).
 *
 * Four, three and two items. "Work" is what was delivered, "Expertise" is what
 * is offered and evidenced, "Background" is where it came from — and the split
 * is what lets ten destinations sit on one row at 769px, which ten flat labels
 * could not do at any width (D-066, D-067).
 */
export const NAV_GROUPS: ReadonlyArray<NavGroup> = [
	{
		id: "work",
		label: "Work",
		items: [
			{ label: "Experience", href: "/#experience" },
			{ label: "Products", to: "/products" },
			{ label: "Projects", to: "/projects" },
			{ label: "Stories", to: "/stories" },
			/* Added D-094. UNDER WORK, because what it draws is the delivery
			   record — roles, tenure, the delivery figures and the build
			   portfolio — and "Work" is what was delivered. It costs the navbar
			   nothing: D-068 fitted exactly four TOP-LEVEL labels on one row at
			   769px, and an item inside an existing dropdown adds no label.
			   Group sizes go 5/5/3. */
			{ label: "Dashboard", to: "/dashboard" }
		]
	},
	{
		id: "expertise",
		label: "Expertise",
		items: [
			{ label: "Services", href: "/#services" },
			{ label: "Skills", href: "/#skills" },
			{ label: "Certifications", href: "/#certifications" },
			/* Added D-076. UNDER EXPERTISE, NOT WORK: this group is what is
			   offered and evidenced, and teaching a subject is evidence of it —
			   "Work" is what was delivered for an employer or a client, which a
			   workshop is not. It also costs the navbar NOTHING, which is the
			   part that mattered: D-068 fitted exactly four top-level labels on
			   one row at 769px after three decisions had spent every lever on
			   that control, and an item inside an existing dropdown adds no
			   label. Group sizes go 4/4/3. */
			{ label: "Workshops", to: "/workshops" },
			/* Added D-094. UNDER EXPERTISE rather than Work, on the Workshops
			   precedent: this route delivers nothing new, it gathers the AI
			   credentials, the framework, the builds and the session into one
			   view of a capability — which is what this group is for. */
			{ label: "AI & Innovation", to: "/ai-innovation" }
		]
	},
	{
		id: "background",
		label: "Background",
		items: [
			{ label: "Education", href: "/#education" },
			{ label: "Martial Arts", to: "/martial-arts" },
			/* Added D-069. One edit, three navigations — the desktop dropdown,
			   the mobile menu and the footer all read this list, which is what
			   D-068 built it for. Before that this was three separate literals
			   and the label would have had to be added to each. */
			{ label: "Recognition", to: "/achievements" }
		]
	}
];

/**
 * Every destination, flattened, in navigation order.
 *
 * The Footer renders THIS rather than the grouped shape, deliberately — see
 * the note on `FOOTER_NAV` in `Footer.tsx`. Derived rather than re-listed, so
 * a destination cannot exist in one navigation and not the other: that is the
 * whole point of this file.
 */
export const NAV_DESTINATIONS: ReadonlyArray<NavDestination> = [
	NAV_ABOUT,
	...NAV_GROUPS.flatMap((group: NavGroup) => group.items)
];
